/**
 * InvoicesPage (REV-040 → REV-049).
 *
 * Lists the tenant's invoices (GET /invoices) with status badges and per-row
 * Approve/Submit lifecycle actions (POST /invoices/{id}:approve|:submit). When
 * the list endpoint is not yet deployed, the page shows a graceful empty state
 * and points finance at the Revenue console. Data wiring (useInvoices) unchanged.
 */
import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useInvoices } from '../hooks/useInvoices';
import type { RevenueInvoice } from '../hooks/useRevenue';
import { colors } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { EmptyState } from '../components/EmptyState';

function money(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return '—';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function InvoicesPage() {
  const { invoices, isLoading, isWorking, error, unavailable, refresh, approveInvoice, submitInvoice } =
    useInvoices();

  const columns: Column<RevenueInvoice>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      render: (inv) => <span style={{ fontWeight: 600 }}>{inv.invoice_number ?? inv.invoice_id}</span>,
    },
    {
      key: 'case',
      header: 'Case',
      render: (inv) =>
        inv.case_id ? (
          <Link to={`/cases/${inv.case_id}`} style={link}>
            {inv.case_id}
          </Link>
        ) : (
          <span style={muted}>—</span>
        ),
    },
    { key: 'status', header: 'Status', render: (inv) => <StatusBadge value={inv.status} /> },
    { key: 'total', header: 'Total', align: 'right', render: (inv) => money(inv.total) },
    { key: 'balance', header: 'Balance', align: 'right', render: (inv) => money(inv.balance_due) },
    {
      key: 'actions',
      header: 'Actions',
      render: (inv) => {
        const canApprove = inv.status === 'draft';
        const canSubmit = inv.status === 'in_review' || inv.status === 'approved';
        return (
          <div style={actions}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => approveInvoice(inv.invoice_id)}
              disabled={isWorking || !canApprove}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => submitInvoice(inv.invoice_id)}
              disabled={isWorking || !canSubmit}
            >
              Submit
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle={
          <>
            Review and advance invoices through the finance lifecycle (REV-040 → REV-049). Generate new
            invoices and view AR aging in the <Link to="/revenue" style={link}>Revenue console</Link>.
          </>
        }
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      <Card title="All invoices" padded={false}>
        {(isLoading || isWorking) && (
          <div style={{ padding: 18 }}>
            <LoadingIndicator text={isWorking ? 'Working…' : 'Loading invoices…'} />
          </div>
        )}
        {error && (
          <p style={errorText} role="alert">
            {error.message}
          </p>
        )}
        {unavailable && (
          <EmptyState
            title="Invoice list not available yet"
            description="Use the Revenue console to generate and manage invoices."
          />
        )}
        {!isLoading && !error && !unavailable && (
          <DataTable columns={columns} rows={invoices} rowKey={(inv) => inv.invoice_id} emptyTitle="No invoices yet" />
        )}
      </Card>
    </>
  );
}

const link: CSSProperties = { color: colors.action, fontWeight: 600 };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const actions: CSSProperties = { display: 'flex', gap: 6 };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', padding: 18 };
